import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { AdminApiService } from '../admin-api.service';
import {
  SystemOwnerSystem,
  SystemOwnerSystemDetails,
  SystemOwnerUser,
} from '../models';

@Component({
  standalone: true,
  selector: 'app-system-owner-page',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DropdownModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './system-owner.page.html',
  styleUrls: ['./system-owner.page.scss'],
})
export class SystemOwnerPage implements OnInit {
  private api = inject(AdminApiService);
  private ms = inject(MessageService);

  systems = signal<SystemOwnerSystem[]>([]);
  details = signal<SystemOwnerSystemDetails | null>(null);
  loadingSystems = signal(false);
  loadingDetails = signal(false);
  saving = signal(false);

  selectedSystemKey = '';
  selectedUserId = '';

  ngOnInit() {
    this.loadSystems();
  }

  loadSystems() {
    this.loadingSystems.set(true);
    this.api.listSystemOwnerSystems().subscribe({
      next: (systems) => {
        this.systems.set(systems || []);
        this.loadingSystems.set(false);

        if (!this.selectedSystemKey && systems?.length) {
          this.selectedSystemKey = systems[0].key;
          this.loadDetails();
        }
      },
      error: () => {
        this.loadingSystems.set(false);
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشل تحميل الأنظمة' });
      },
    });
  }

  loadDetails() {
    if (!this.selectedSystemKey) return;
    this.loadingDetails.set(true);
    this.selectedUserId = '';

    this.api.getSystemOwnerSystem(this.selectedSystemKey).subscribe({
      next: (details) => {
        this.details.set(details);
        this.loadingDetails.set(false);
      },
      error: () => {
        this.details.set(null);
        this.loadingDetails.set(false);
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشل تحميل تفاصيل النظام' });
      },
    });
  }

  addSuperAdmin() {
    if (!this.selectedSystemKey || !this.selectedUserId) return;
    this.saving.set(true);

    this.api.addSystemSuperAdmin(this.selectedSystemKey, this.selectedUserId).subscribe({
      next: () => {
        this.ms.add({ severity: 'success', summary: 'تم', detail: 'تمت إضافة سوبر أدمن للنظام' });
        this.saving.set(false);
        this.loadSystems();
        this.loadDetails();
      },
      error: () => {
        this.saving.set(false);
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشلت إضافة سوبر أدمن' });
      },
    });
  }

  removeSuperAdmin(user: SystemOwnerUser) {
    if (!this.selectedSystemKey) return;
    this.saving.set(true);

    this.api.removeSystemSuperAdmin(this.selectedSystemKey, user.id).subscribe({
      next: () => {
        this.ms.add({ severity: 'success', summary: 'تم', detail: 'تمت إزالة سوبر أدمن' });
        this.saving.set(false);
        this.loadSystems();
        this.loadDetails();
      },
      error: () => {
        this.saving.set(false);
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشلت إزالة سوبر أدمن' });
      },
    });
  }

  candidateOptions() {
    const selected = new Set((this.details()?.superAdmins || []).map((u) => u.id));
    return (this.details()?.candidateUsers || [])
      .filter((u) => !selected.has(u.id))
      .map((u) => ({
        label: `${u.fullName || u.username}${u.email ? ' - ' + u.email : ''}`,
        value: u.id,
      }));
  }

  userLabel(user: SystemOwnerUser) {
    return user.fullName || user.username;
  }

  statusLabel(user: SystemOwnerUser) {
    if (user.isLocked) return 'مقفل';
    return user.isActive ? 'نشط' : 'معطل';
  }

  statusSeverity(user: SystemOwnerUser): 'success' | 'danger' | 'warning' {
    if (user.isLocked) return 'warning';
    return user.isActive ? 'success' : 'danger';
  }

  trackSystem = (_: number, item: SystemOwnerSystem) => item.key;
  trackUser = (_: number, item: SystemOwnerUser) => item.id;
}
